import { Text } from "@/components/ui";
import type { TextProps } from "@/components/ui/text";
import type { ResponsiveVariantProps } from "@/lib/styles";
import { cva } from "class-variance-authority";

export type TypographyVariants = {
  variant: {
    h1: string;
    h2: string;
    h3: string;
    "label-lg": string;
    "label-md": string;
    "label-sm": string;
    "body-lg": string;
    "body-md": string;
    "body-sm": string;
    "caption-md": string;
    "caption-sm": string;
  };
  color: {
    "brand-primary": string;
    "brand-static": string;
    "button-cta": string;
    "button-cta2-hover": string;
    "foreground-intermediate": string;
    "foreground-default": string;
    "foreground-attenuated": string;
    "muted-foreground": string;
    "secondary-text": string;
    "success-on-surface": string;
    white: string;
    danger: string;
    success: string;
    info: string;
    warning: string;
    "warning-on-surface": string;
  };
  align: {
    left: string;
    center: string;
    right: string;
  };
  family: {
    inter: string;
    epilogue: string;
  };
  fontStyle: {
    italic: string;
    normal: string;
  };
};

const typographyVariants = cva<TypographyVariants>("font-inter", {
  variants: {
    variant: {
      h1: "text-[28px] font-semibold leading-[36px] md:text-[48px] md:leading-[56px]",
      h2: "text-[24px] font-semibold leading-[32px] md:text-[32px] md:leading-[52px]",
      h3: "text-[20px] font-semibold leading-[28px] md:text-[28px] md:leading-[40px]",
      "label-lg": "text-[18px] font-medium leading-[26px] md:text-[20px] md:leading-[28px]",
      "label-md": "text-[16px] font-medium leading-[24px] md:text-[18px] md:leading-[26px]",
      "label-sm": "text-[14px] font-medium leading-[20px] md:text-[16px] md:leading-[24px]",

      "body-lg": "text-[16px] font-normal leading-[24px] md:text-[18px] md:leading-[26px]",
      "body-md": "text-[14px] font-normal leading-[20px] md:text-[16px] md:leading-[24px]",
      "body-sm": "text-[12px] font-normal leading-[16px] md:text-[14px] md:leading-[20px]",

      "caption-md": "text-[10px] font-normal leading-[14px] md:text-[12px] md:leading-[16px]",
      "caption-sm": "text-[8px] font-normal leading-[12px] md:text-[10px] md:leading-[14px]",
    },
    color: {
      "brand-primary": "color-brand-primary",
      "brand-static": "color-brand-static",
      "button-cta": "color-button-cta",
      "button-cta2-hover": "color-button-cta2-hover",
      "foreground-intermediate": "text-foreground-intermediate",
      "foreground-default": "text-foreground",
      "foreground-attenuated": "text-foreground-attenuated",
      "muted-foreground": "text-muted-foreground",
      "secondary-text": "text-secondary-text",
      white: "color-white",
      danger: "color-danger",
      success: "text-success",
      "success-on-surface": "text-success-on-surface",
      info: "text-info",
      warning: "text-warning",
      "warning-on-surface": "text-warning-on-surface",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
    family: {
      inter: "font-inter",
      epilogue: "font-epilogue",
    },
    fontStyle: {
      italic: "italic",
      normal: "normal",
    },
  },
  defaultVariants: {
    variant: "body-lg",
    color: "foreground-default",
    align: "left",
    fontStyle: "normal",
  },
});

interface TypographyProps extends ResponsiveVariantProps<typeof typographyVariants>, TextProps {}

export function Typography({
  color,
  variant,
  align,
  family,
  fontStyle,
  className,
  ...props
}: TypographyProps) {
  const style = typographyVariants({
    color,
    variant,
    align,
    className,
    family,
    fontStyle,
  });

  return <Text {...props} className={style} />;
}
